import { formatToCompanyTimezone, formatToUtc, getDateTimeFromSelectedDate, getYearFromCompanyDate } from "../lib/functions";
import dayjs from 'dayjs';
import Big from "big.js";
import { bigMul, bigPercent, toBig } from "../lib/bigConversion";

// Discount for a single item row: reads whichever mode is active
// (percent, the default/undefined-safe mode, or a flat amount), clamped
// so a flat amount can never exceed the item's own line total.
export const getItemDiscountAmount = (item, line) => {
  if (item?.discountMode === "amount") {
    const raw = toBig(item?.discount_amt);
    return raw.gt(line) ? line : raw.lt(0) ? new Big(0) : raw;
  }
  return bigPercent(line, item?.discount_per || 0);
};

export const createSalesPayload = (
  customer,
  itemsList,
  footerValues,
  taxMode,
  prevDues,
  employee,
  selectedSalesAcc,
  selectedDiscountAcc,
  selectedTaxAcc,
  selectedTransportAcc,
  tag_field_id,
  entryDate,
  deliveryData = {},
  isTatmeenProduct = false,
  ssccCode = null,
  selectedServiceAcc = null,
) => {
  const { totalSubtotal, discountAmount, taxAmount, grandTotal, dueAmount } = calculateTotals(
    taxMode,
    itemsList,
    footerValues
  );



  // convert selected/entry Date to utc date + time
  let finalEntryDate = entryDate;
  if (!dayjs(entryDate).isSame(dayjs(), "day")) {
    finalEntryDate = getDateTimeFromSelectedDate(entryDate); // Merge date + current time in TZ

  } else {
    finalEntryDate = entryDate; // Use now in TZ
  }

  let dateUTC = formatToUtc(finalEntryDate);
  let companydate = formatToCompanyTimezone(dateUTC);
  const vchYear = getYearFromCompanyDate(companydate);

  const items = mapItems(
    itemsList,
    taxMode,
    selectedDiscountAcc,
    selectedTaxAcc,
    selectedTransportAcc
  );
  const payCart = footerValues?.payCart
    ? mapPayCart(footerValues?.payCart)
    : [];
  const payServiceCart = footerValues?.payServiceCart
    ? mapAddExpensesCart(footerValues?.payServiceCart)
    : [];

  const prevDueBig = new Big(prevDues || 0);
  const grandTotalBig = new Big(grandTotal || 0);
  const dueBig = new Big(dueAmount || 0);

  const emis = footerValues?.emis ? mapEmis(footerValues?.emis) : [];
  const msg = `Dear ${customer.acc_name}, Your Bill Amount is ${grandTotalBig.toFixed(2)} (${footerValues?.salesVoucherNumber
    }), Bill Paid Amount: ${footerValues?.paidAmount
    }, Bill Due Amount: ${dueBig.toFixed(2)}, Your Previous Due is ${prevDueBig.toFixed(2)} & Current Due is ${prevDueBig.plus(dueBig).toFixed(2)
    }.`;

  // let totalTax = itemsList.reduce((prev, curr) => {
  //   return prev + parseFloat(curr.vat);
  // }, 0);

  // let totalDiscount = itemsList.reduce((prev, curr) => {
  //   return prev + parseFloat(curr.discount);
  // }, 0);

  let totalTax = itemsList.reduce((sum, item) => {
    if (item?.bonus) return sum;

    const qty = toBig(item.quantity);
    const rate = toBig(item.rate);
    const vatPer = toBig(item.vat_per);

    const line = bigMul(qty, rate);
    const discountAmount = getItemDiscountAmount(item, line);
    const taxableBase = line.minus(discountAmount);
    const vatAmount = taxableBase.times(vatPer).div(100);

    return new Big(sum).plus(vatAmount);
  }, new Big(0));


  let totalDiscount = itemsList.reduce((sum, item) => {
    if (item?.bonus) return sum;

    const qty = new Big(item.quantity || 0);
    const rate = new Big(item.rate || 0);
    const discountAmt = getItemDiscountAmount(item, qty.times(rate));
    return new Big(sum).plus(discountAmt);
  }, new Big(0));

  const other_service_cost = payServiceCart?.reduce(
    (sum, item) => new Big(sum).plus(new Big(item?.tran_amount || 0)),
    new Big(0)
  );

  return {
    sale_id: 0,
    orderId: 0,
    itemCart: items,
    emis: emis,
    emi_month: footerValues?.emiMonths,
    payCart: payCart,
    payServiceCart: payServiceCart,
    is_smg: "yes",
    msg: msg,
    vchdate: vchYear,
    customer: {
      acc_id: customer.acc_id,
      acc_name: customer.acc_name,
      institution_name: customer.institution_name,
      contact_no: customer.contact_no,
      country_code: customer.country_code,
      address: customer.address,
    },
    masterData: {
      sale_voucher_no: footerValues?.salesVoucherNumber,
      is_serial: "no",
      is_tatmeen_product: isTatmeenProduct ? 1 : 0,
      sscc_code: ssccCode || null,
      is_cal_type: taxMode === "individual" ? taxMode : "on_total",
      sales_acc_id: selectedSalesAcc.acc_id,
      discount_acc_id: selectedDiscountAcc.acc_id,
      tax_acc_id: selectedTaxAcc.acc_id,
      transport_acc_id: selectedTransportAcc.acc_id,
      service_acc_id: selectedServiceAcc?.acc_id || null,
      narration: footerValues.description,
      created_date: formatToUtc(finalEntryDate),
      due_date: dueAmount > 0 ? footerValues?.dueDate : "",

      is_condition_sale: "no",
      challan_text: footerValues?.challanText,
      total_discount_mode: footerValues?.discountMode === "amount" ? "amount" : "percent",
      sub_total: totalSubtotal,
      total_discount_per: footerValues.discountPercent,

      total_discount:
        taxMode === "individual"
          ? totalDiscount.toString()
          : new Big(discountAmount || 0).toString(),
      total_tax_per: footerValues?.tax_per ? footerValues?.tax_per : 0,
      // total_tax:
      //   taxMode === "individual" ? totalTax : taxAmount ? taxAmount : 0,
      total_tax:
        taxMode === "individual"
          ? totalTax.toString()
          : new Big(taxAmount || 0).toString(),

      total_transport_cost: 0,
      total_amount: grandTotal,
      paid_amount: footerValues?.paidAmount ? new Big(footerValues?.paidAmount).toString() : 0,
      due_amount: dueAmount ? dueAmount : 0,
      is_emi: footerValues?.emis?.length > 0 ? "yes" : "no",
      emi_month: footerValues?.emiMonths,
      day_week: footerValues?.emiDayWeek || 0,
      emi_amount: footerValues?.emiAmount || 0,
      employee_id: employee,
      cus_po_ref: null,
      other_service_cost: other_service_cost.toString(),
      conversion_rate: footerValues.conversion_rate || 0,
      conversion_currency: footerValues.conversion_currency || null,
      ...(tag_field_id && tag_field_id.length > 0
        ? {
          tag_field_id: tag_field_id,
        }
        : {}),
      ...(deliveryData?.delivery_type
        ? {
          delivery_type: deliveryData.delivery_type,
          delivery_route_id: deliveryData.delivery_route_id || null,
          pickup_date: deliveryData.pickup_date || null,
          delivery_date: deliveryData.delivery_date || null,
          delivery_notes: deliveryData.delivery_notes || null,
        }
        : {}),
    },
  };
};

export const mapItems = (
  itemList,
  taxMode,
  selectedDiscountAcc,
  selectedTaxAcc,
  selectedTransportAcc
) => {
  return itemList?.map((el) => {
    const isBonus = Boolean(el?.bonus);

    const qty = new Big(el?.quantity || 0);

    const rate = isBonus ? new Big(0) : new Big(el?.rate || 0);

    const line = qty.times(rate);

    const discountAmt = isBonus ? new Big(0) : getItemDiscountAmount(el, line);
    const afterDiscount = line.minus(discountAmt);

    // Back-computed so the printed voucher (which reads this field directly
    // in individual calc mode) is correct even when the row is in amount mode.
    const discountPer = isBonus
      ? new Big(0)
      : el?.discountMode === "amount"
        ? (line.eq(0) ? new Big(0) : discountAmt.div(line).times(100))
        : new Big(el?.discount_per || 0);

    const taxPer = isBonus ? new Big(0) : new Big(el?.vat_per || 0);
    const taxPerSafe = new Big(taxPer || 0);

    const taxAmt = isBonus
      ? new Big(0)
      : ((afterDiscount.times(taxPerSafe)).div(100));

    const itemTotal =
      taxMode === "individual"
        ? afterDiscount.plus(taxAmt)
        : line;

    return {
      sale_d_id: el?.sale_d_id || null,
      // warehouse
      warehouse_id: 0,
      warehouse_name: "",

      // item
      item_id: el?.product?.item_id || 0,
      item_name: el?.product?.item_name || "",
      display_text: el?.product?.display_text || "",
      item_description: el?.item_description || "",
      is_serial: el?.product?.is_serial || "no",

      // qty & rate
      item_qty: qty.toString(),
      item_rate: rate.toString(),

      // discount
      item_discount_per: discountPer.toString(),
      item_discount: discountAmt.toString(),
      discount_mode: el?.discountMode === "amount" ? "amount" : "percent",

      discount_acc_id: selectedDiscountAcc?.acc_id || 0,
      discount_acc_name: selectedDiscountAcc?.acc_name || "",

      // tax
      item_tax_per: taxPer.toString(),
      item_tax: taxAmt.toString(),

      tax_acc_id: selectedTaxAcc?.acc_id || 0,
      tax_acc_name: selectedTaxAcc?.acc_name || "",

      // total
      item_total: itemTotal.toString(),

      // stock / sales mapping
      retail_qty: 0,
      sale_qty: qty.times(new Big(el?.product?.conversion || 1)).toString(),
      sale_rate:
        (Number(el?.product?.conversion) || 1) > 1
          ? rate.div(new Big(el?.product?.conversion || 1)).toString()
          : rate.toString(),

      // display helpers
      qty_display:
        qty +
        " " +
        (el?.product?.unit_symbol || "") +
        ((Number(el?.product?.conversion) || 1) > 1
          ? ", 0 " + (el?.product?.base_unit_name || "")
          : ""),

      unit_symbol: el?.product?.unit_symbol || "",
      base_unit_name: el?.product?.base_unit_name || "",
      unit_name: el?.product?.unit_name || "",
      conversion: Number(el?.product?.conversion) || 1,

      // avg rate (safe)
      purchase_average_rate: Number(el?.itemInfo?.average_rate) || 0,

      units: el?.product?.units || [],
      per_unit_symbol: el?.product?.unit_symbol || "",
      per_conversion: Number(el?.product?.conversion) || 1,
      per_unit_id: el?.product?.unit_id || 0,

      // bonus flag
      bounus_update_check: isBonus,

      child_sscc_code: el?.child_sscc_code || "",
      is_tatmeen_required: el?.is_tatmeen_required === true ? true : false,
      serial_mode: el?.serial_mode || "unit",
      serials: el?.selectedSerials || [],

      // batch
      ...(el?.batch_id && {
        selected_batch_expiry: el?.batch_expiry || null,
        selected_batch_id: el?.batch_id,
        selected_batch: el?.batch_name || "",
      }),

      // packing
      ...(el?.packing_name && el?.packing_quantity && {
        packing_name: el?.packing_name,
        packing_quantity: el?.packing_quantity,
        packing_name_quantity: el?.packing_name_quantity,
      }),
    };
  });
};


export const mapPayCart = (payCart) => {
  const result = payCart?.map((el) => {
    return {
      to_acc_id: el?.from_acc_id,
      acc_type_id: el?.acc_type_id,
      date: el?.date,
      to_acc_name: el.from_acc_name,
      tran_amount: el?.tran_amount,
      cheque_number: el?.cheque_number || null,
      expected_bank_acc_id: el?.expected_bank_acc_id || null,
    };
  });
  return result;
};

export const mapEmis = (emis) => {
  const result = emis?.map((el) => {
    return {
      emi_no: el?.emiNo,
      from_date: el?.fromDate,
      last_date: el?.lastPayDate,
      amount: el?.amount,
    };
  });
  return result;
};

export const mapAddExpensesCart = (payServiceCart) => {
  const result = payServiceCart?.map((el) => {
    return {
      to_acc_id: el.to_acc_id,
      acc_type_id: el.acc_type_id,
      to_acc_name: el.to_acc_name,
      tran_amount: el?.tran_amount,
    };
  });
  return result;
};

export const calculateTotals = (taxMode, dataSource, footerValues) => {
  let totalSubtotal = new Big(0);

  if (taxMode === "individual") {
    dataSource.forEach((item) => {
      if (item?.bonus) return totalSubtotal.toString();

      const vatPercent = new Big(item.vat_per || 0);
      const qty = new Big(item.quantity || 0);
      const rate = new Big(item.rate || 0);
      const line = qty.times(rate);

      const discountAmount = getItemDiscountAmount(item, line);
      const rowTotal = line.minus(discountAmount);
      const vatAmount = rowTotal.times(vatPercent).div(100);
      totalSubtotal = totalSubtotal.plus(rowTotal.plus(vatAmount));

      // const discountAmount =
      //   (item.discount_per / 100) * item.quantity * item.rate;
      // const rowTotal = item.quantity * item.rate - discountAmount;
      // const vatAmount = (item.vat_per / 100) * rowTotal;
      // totalSubtotal += rowTotal + vatAmount;

    });
  } else {
    totalSubtotal = dataSource?.reduce((sum, item) => {
      if (item?.bonus) return sum;

      const qty = new Big(item.quantity || 0);
      const rate = new Big(item.rate || 0);

      return new Big(sum).plus(qty.times(rate));
    }, new Big(0));
  }
  const hasTaxableItems = dataSource.some(
    item => !item?.bonus && Number(item.vat_per) > 0
  );

  const totalServiceCharge = Array.isArray(footerValues?.payServiceCart)
    ? new Big(footerValues.payServiceCart.reduce(
      (sum, item) => sum.plus(new Big(item?.tran_amount || 0)),
      new Big(0)
    ))
    : new Big(0);

  const discountAmount =
    taxMode === "total"
      ? totalSubtotal.times(footerValues?.discountPercent || 0).div(100)
      : new Big(0);
  const totalAfterDiscount = totalSubtotal.minus(discountAmount);
  const taxAmount =
    taxMode === "total"
      ? totalAfterDiscount.times(footerValues?.taxPercent || 0).div(100)
      : new Big(0);

  const totalIncludingTax = totalAfterDiscount.plus(taxAmount);

  // const grandTotal = totalIncludingTax + footerValues?.serviceCharge;
  const grandTotal = totalAfterDiscount.plus(taxAmount).plus(totalServiceCharge);
  let due = grandTotal.minus(
    new Big(footerValues?.paidAmount || 0)
  );

  // to avoid dues below 0.01 values
  if (due.abs().lt(0.01)) {
    due = new Big(0);
  }


  return {
    totalSubtotal: totalSubtotal.toString(),
    discountAmount: discountAmount.toString(),
    taxAmount: taxAmount.toString(),
    grandTotal: grandTotal.toString(),
    dueAmount: due.toString()
  };
};
